import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Paid } from '@mui/icons-material';
import { anuidadeService, Anuidade } from '../services/anuidadeService';
import { socioService, Socio } from '../services/socioService';

const Anuidades: React.FC = () => {
  const [anuidades, setAnuidades] = useState<Anuidade[]>([]);
  const [socios, setSocios] = useState<Socio[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPagamento, setOpenPagamento] = useState(false);
  const [selectedAnuidade, setSelectedAnuidade] = useState<Anuidade | null>(null);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [formData, setFormData] = useState({
    socio_id: '',
    ano: '',
    data_vencimento: '',
    valor: '',
    observacoes: '',
  });
  const [dataPagamento, setDataPagamento] = useState('');

  const anoAtual = useMemo(() => new Date().getFullYear(), []);
  const hoje = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    loadSocios();
    loadAnuidades();
  }, []);

  const formatApiError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const campos: Record<string, string> = {
        socio_id: 'Sócio',
        ano: 'Ano',
        data_vencimento: 'Data de vencimento',
        valor: 'Valor',
        data_pagamento: 'Data de pagamento',
      };
      return detail
        .map((item) => {
          const fieldKey = item?.loc?.[1] || 'campo';
          const field = campos[fieldKey] || fieldKey;
          const msg = item?.msg || 'erro de validação';
          let msgTraduzida = msg;
          if (msg.includes('field required')) {
            msgTraduzida = 'campo obrigatório';
          } else if (msg.includes('value is not a valid date')) {
            msgTraduzida = 'data inválida';
          } else if (msg.includes('value is not a valid integer')) {
            msgTraduzida = 'número inválido';
          } else if (msg.includes('value is not a valid float')) {
            msgTraduzida = 'valor inválido';
          }
          return `${field}: ${msgTraduzida}`;
        })
        .join('; ');
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  };

  const loadSocios = async () => {
    try {
      const data = await socioService.listar();
      setSocios(data);
    } catch (err: any) {
      setError(formatApiError(err, 'Erro ao carregar sócios'));
    }
  };

  const loadAnuidades = async () => {
    try {
      setLoading(true);
      const data = await anuidadeService.listar();
      setAnuidades(data);
    } catch (err: any) {
      setError(formatApiError(err, 'Erro ao carregar anuidades'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      socio_id: '',
      ano: String(anoAtual),
      data_vencimento: '',
      valor: '',
      observacoes: '',
    });
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      socio_id: '',
      ano: '',
      data_vencimento: '',
      valor: '',
      observacoes: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.socio_id) {
      setError('Selecione um sócio');
      return;
    }
    if (!formData.ano) {
      setError('Informe o ano');
      return;
    }
    if (!formData.data_vencimento) {
      setError('Informe a data de vencimento');
      return;
    }
    if (!formData.valor) {
      setError('Informe o valor');
      return;
    }

    try {
      setError('');
      await anuidadeService.criar({
        socio_id: Number(formData.socio_id),
        ano: Number(formData.ano),
        data_vencimento: formData.data_vencimento,
        valor: Number(formData.valor),
        observacoes: formData.observacoes || undefined,
      });
      handleCloseDialog();
      loadAnuidades();
    } catch (err: any) {
      setError(formatApiError(err, 'Erro ao salvar anuidade'));
    }
  };

  const handleOpenPagamento = (anuidade: Anuidade) => {
    setSelectedAnuidade(anuidade);
    setDataPagamento(hoje);
    setError('');
    setOpenPagamento(true);
  };

  const handleClosePagamento = () => {
    setOpenPagamento(false);
    setSelectedAnuidade(null);
    setDataPagamento('');
  };

  const handlePagar = async () => {
    if (!selectedAnuidade?.id) return;
    if (!dataPagamento) {
      setError('Informe a data de pagamento');
      return;
    }

    try {
      setError('');
      await anuidadeService.pagar(selectedAnuidade.id, dataPagamento);
      handleClosePagamento();
      loadAnuidades();
    } catch (err: any) {
      setError(formatApiError(err, 'Erro ao marcar como pago'));
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const formatValor = (valor?: number) => {
    if (valor === undefined || valor === null) return '-';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Pago':
        return 'success';
      case 'Vencido':
        return 'error';
      default:
        return 'warning';
    }
  };

  const anuidadesFiltradas = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return anuidades;
    return anuidades.filter((anuidade) => {
      const socioNome = anuidade.socio?.nome?.toLowerCase() || '';
      const socioCodigo = anuidade.socio?.codigo?.toLowerCase() || '';
      const ano = String(anuidade.ano || '').toLowerCase();
      const status = (anuidade.status || '').toLowerCase();
      return (
        socioNome.includes(termo) ||
        socioCodigo.includes(termo) ||
        ano.includes(termo) ||
        status.includes(termo)
      );
    });
  }, [anuidades, filtro]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
        mb={2}
      >
        <Typography variant="h4">Anuidades</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
          Nova Anuidade
        </Button>
      </Box>
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={3}>
        <TextField
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Pesquisar por sócio, ano ou status"
          size="small"
          sx={{ maxWidth: { xs: '100%', sm: 360 } }}
          fullWidth
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : anuidadesFiltradas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma anuidade encontrada
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sócio</TableCell>
                <TableCell>Ano</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {anuidadesFiltradas.map((anuidade) => (
                <TableRow key={anuidade.id}>
                  <TableCell>
                    {anuidade.socio ? `${anuidade.socio.codigo} - ${anuidade.socio.nome}` : '-'}
                  </TableCell>
                  <TableCell>{anuidade.ano}</TableCell>
                  <TableCell>{formatDate(anuidade.data_vencimento)}</TableCell>
                  <TableCell>{formatValor(anuidade.valor)}</TableCell>
                  <TableCell>
                    <Chip
                      label={anuidade.status || 'Pendente'}
                      color={getStatusColor(anuidade.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(anuidade.data_pagamento)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPagamento(anuidade)}
                      disabled={anuidade.status === 'Pago'}
                      title="Marcar como pago"
                    >
                      <Paid />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Anuidade</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Selecione um sócio</InputLabel>
                <Select
                  value={formData.socio_id}
                  label="Selecione um sócio"
                  onChange={(e) => setFormData({ ...formData, socio_id: String(e.target.value) })}
                >
                  {socios.map((socio) => (
                    <MenuItem key={socio.id} value={socio.id}>
                      {socio.codigo} - {socio.nome_completo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Ano"
                type="number"
                value={formData.ano}
                onChange={(e) => setFormData({ ...formData, ano: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Data de Vencimento"
                type="date"
                value={formData.data_vencimento}
                onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Valor"
                type="number"
                inputProps={{ step: '0.01' }}
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPagamento} onClose={handleClosePagamento} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar como pago</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Data de Pagamento"
            type="date"
            value={dataPagamento}
            onChange={(e) => setDataPagamento(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePagamento}>Cancelar</Button>
          <Button onClick={handlePagar} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Anuidades;

